import { queryOptions, useInfiniteQuery } from "@tanstack/react-query";

import { api } from "@/api/api";
import type { ReservationType } from "@/features/bookings/types/types";
import type {
  FindUserReservationOutputDto,
  FindUserReservationsOutputDto,
} from "@/types/codegen";
import type { ServiceType } from "@/types/types";

const PAGE_SIZE = 5;

type Props = {
  service: ServiceType;
  searchFilter: ReservationType;
};

export const useReservations = ({ service, searchFilter }: Props) => {
  const params = { perPage: PAGE_SIZE, service, searchFilter };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching, isError } =
    useInfiniteQuery({
      queryKey: ["user", "reservations", params] as const,
      queryFn: ({ pageParam, signal }) =>
        api
          .get<FindUserReservationsOutputDto>("/user/reservations", {
            params: {
              ...params,
              page: pageParam,
            },
            signal,
          })
          .then((res) => res.data),
      initialPageParam: 1,
      staleTime: 0,
      gcTime: 0,
      getNextPageParam: (lastPage, pages) => {
        const maxNumberOfPages = Math.ceil(
          lastPage.data.count / params.perPage,
        );
        return pages.length < maxNumberOfPages ? pages.length + 1 : undefined;
      },
    });

  const hasReachedEnd = !hasNextPage;

  return {
    data,
    isLoading,
    isFetching,
    isError,
    hasReachedEnd,
    fetchNextPage,
    searchFilter,
  };
};

export const reservationQueryOptions = (reservationId: string | undefined) =>
  queryOptions({
    queryKey: ["reservation", reservationId],
    queryFn: ({ signal }) => {
      return api.get<FindUserReservationOutputDto>(
        `/user/reservations/${reservationId}`,
        {
          signal,
        },
      );
    },
    select: (response) => response.data?.data,
    enabled: Boolean(reservationId),
    refetchInterval: (query) => {
      const response = query.state.data;

      const reservations = [
        response?.data.data.hotelReservation,
        response?.data.data.flightReservation,
      ].filter((x): x is NonNullable<typeof x> => Boolean(x));

      // we need to wait until all reservations are created
      if (
        reservations.some((reservation) => reservation.status === "IN_PROGRESS")
      ) {
        return 3000;
      }

      return false;
    },
  });
